import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, ErrorState, Icon, Skeleton } from "@sagot/ui";
import { useWorkspaceContext, useWorkspaceId } from "../../../session/workspace-context";
import { serviceKeys } from "../../service/service-keys";
import { useServiceAreaAccess } from "../../service/use-service-area";
import { useServiceQuery } from "../../service/use-service-query";
import { useFormatters } from "../shared";
import { ServiceUsageMore } from "./service-usage-more";
import { UsageMeter } from "./usage-meter";

const MB_PER_GB = 1024;
const USAGE_STALE_MS = 60_000;

/** Storage as the plan counts it (MB), told in GB from 1 GB up. */
export function storageMbText(megabytes: number, t: (key: string, options: { value: string }) => string, format: (value: number) => string): string {
  return megabytes >= MB_PER_GB ? t("service.cards.billing.gb", { value: format(Math.round((megabytes / MB_PER_GB) * 10) / 10) }) : t("service.cards.billing.mb", { value: format(megabytes) });
}

/**
 * "Service Desk usage" on the billing overview (PLN-005): tickets this month against the fair-use allowance and attachment
 * storage against the plan, plus the read-only notice of a plan that lost the desk. Only in workspaces that have the Service Desk
 * (the nav says so), so every other workspace makes no extra request.
 */
export function ServiceUsageCard() {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const ws = useWorkspaceId();
  const { summary } = useWorkspaceContext();
  const access = useServiceAreaAccess("tickets");
  const usage = useServiceQuery({
    queryKey: [...serviceKeys.all(ws), "usage"],
    enabled: access.enabled,
    staleTime: USAGE_STALE_MS,
    request: (api, signal) => api.GET("/v1/service-usage", { signal }),
  });
  if (!access.enabled) return null;
  const tr = (key: string, options: { value: string }) => t(key, options);
  const of = (limit: number | null) => (limit === null ? t("service.cards.billing.unlimited") : t("service.cards.billing.of", { value: fmt.number(limit) }));
  const data = usage.data;
  return (
    <section className="card" aria-labelledby="service-usage-title" data-testid="service-usage-card">
      <div className="card-header">
        <div>
          <h2 id="service-usage-title" className="card-title">
            {t("service.cards.billing.title")}
          </h2>
          <p className="mt-0.5 text-xs text-fg-subtle">{t("service.cards.billing.subtitle")}</p>
        </div>
        {!access.locked ? (
          <Button asChild variant="ghost" size="sm">
            <AppLink href={consoleRoutes.serviceReports(summary.slug)}>
              <Icon name="chart-column" />
              {t("service.cards.billing.reports")}
            </AppLink>
          </Button>
        ) : null}
      </div>
      {usage.isPending ? (
        <div className="card-body grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2" aria-busy="true">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      ) : usage.isError || !data ? (
        <ErrorState headingLevel={3} title={t("service.cards.billing.loadError")} retryLabel={t("service.cards.billing.retry")} onRetry={() => void usage.refetch()} />
      ) : (
        <div className="card-body space-y-4">
          {data.mode !== "full" ? (
            <p role="status" className="flex items-start gap-2 rounded-md bg-warning-soft px-3 py-2 text-sm text-warning" data-testid="service-usage-mode">
              <Icon name="alert-triangle" className="mt-0.5 size-4 shrink-0" />
              {data.mode === "closed" ? t("service.cards.billing.closed") : data.read_only_until ? t("service.cards.billing.readOnly", { date: fmt.date(data.read_only_until) }) : t("service.cards.billing.readOnlyNoDate")}
            </p>
          ) : null}
          <div className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
            <UsageMeter
              label={t("service.cards.billing.tickets")}
              used={data.tickets.used}
              limit={data.tickets.limit}
              usedLabel={fmt.number(data.tickets.used)}
              limitLabel={of(data.tickets.limit)}
              hint={t("service.cards.billing.ticketsHint")}
            />
            <UsageMeter
              label={t("service.cards.billing.storage")}
              used={data.storage_mb.used}
              limit={data.storage_mb.limit}
              usedLabel={storageMbText(data.storage_mb.used, tr, fmt.number)}
              limitLabel={data.storage_mb.limit === null ? t("service.cards.billing.unlimited") : `/ ${storageMbText(data.storage_mb.limit, tr, fmt.number)}`}
              hint={t("service.cards.billing.storageHint")}
            />
            <ServiceUsageMore data={data} />
          </div>
        </div>
      )}
    </section>
  );
}
